import React, { useEffect, useMemo } from "react";
import { checkFileSize } from "../../utils/media";
import DownloadButton from "./DownloadButton";
import "./MediaField.css";

/**
 * File picker with size validation, file name, live preview and a download
 * button for the currently saved file.
 *
 * - `file`: the newly chosen File (or null)
 * - `existingUrl`: URL already saved on the server (shown when no new file is chosen)
 * - `onFileSelect(file)`: called only when the chosen file passes the size check
 *
 * The native input is hidden, so "required" is checked in the page's submit handler.
 */
const MediaField = ({
  id,
  label,
  type = "image",
  file,
  existingUrl,
  onFileSelect,
  maxMB,
  optional = false,
}) => {
  const isVideo = type === "video";

  const objectUrl = useMemo(
    () => (file instanceof File ? URL.createObjectURL(file) : null),
    [file]
  );
  useEffect(() => () => objectUrl && URL.revokeObjectURL(objectUrl), [objectUrl]);

  const previewUrl = objectUrl || existingUrl || null;

  const handleChange = (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!chosen) return;
    if (!checkFileSize(chosen, maxMB, isVideo ? "Video" : "Image")) return;
    onFileSelect(chosen);
  };

  const status = file
    ? file.name
    : existingUrl
    ? "Current file will be kept"
    : "No file chosen";

  return (
    <div className="form-group media-field">
      <label htmlFor={id}>
        {label}
        {optional && <span className="media-field-optional"> (optional)</span>}
      </label>

      <input
        type="file"
        id={id}
        accept={isVideo ? "video/*" : "image/*"}
        onChange={handleChange}
        className="media-field-input"
      />

      <div className="media-field-picker">
        <label htmlFor={id} className="custom-file-label media-field-btn">
          {previewUrl ? "Change" : "Choose"} {isVideo ? "Video" : "Image"}
        </label>
        <span className="media-field-status" title={status}>
          {status}
        </span>
      </div>
      <small className="media-field-hint">
        {maxMB ? `Max ${maxMB} MB · ` : "Any size · "}{isVideo ? "any video format" : "any image format"}
      </small>

      {previewUrl && (
        <div className="media-field-preview">
          {isVideo ? (
            <video src={previewUrl} controls muted />
          ) : (
            <img src={previewUrl} alt={`${label} preview`} />
          )}
          {file && <span className="media-field-badge">New</span>}
          {!file && existingUrl && (
            <DownloadButton url={existingUrl} className="media-field-download" />
          )}
        </div>
      )}
    </div>
  );
};

export default MediaField;
